import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import {
  PROSPECT_PAIN_TYPES,
  PROSPECT_PAIN_TYPE_LABELS,
  PROSPECT_SIGNAL_STATUSES,
  PROSPECT_SIGNAL_STATUS_LABELS,
  type ProspectPainTypeValue,
  type ProspectSignalStatus,
} from '@crm/shared';
import { Button, EmptyState, Input, Modal, Skeleton } from '../../../ui';
import { toast } from '../../../ui/toast';
import { useProduct } from '../../../contexts/ProductContext';
import { productsService } from '../../../services/products.service';
import { usersService } from '../../../services/users.service';
import {
  prospectSignalsService,
  type ProspectSignal,
} from '../../../services/prospect-signals.service';
import { SignalCard } from '../components/SignalCard';
import { PromoteSignalModal } from '../components/PromoteSignalModal';

const DEFAULT_MIN_SCORE = 70;

const selectClass =
  'rounded-md border border-border bg-surface px-3 py-2 text-sm ' +
  'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent';

function errorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { error?: string } } }).response;
  return response?.data?.error ?? 'Ocorreu um erro inesperado';
}

export function ProspectingPage() {
  const { selectedProductId } = useProduct();
  const queryClient = useQueryClient();

  const [minScore, setMinScore] = useState<number>(DEFAULT_MIN_SCORE);
  const [painType, setPainType] = useState<ProspectPainTypeValue | ''>('');
  const [status, setStatus] = useState<ProspectSignalStatus>('pending');
  const [page, setPage] = useState(1);

  const [toPromote, setToPromote] = useState<ProspectSignal | null>(null);
  const [toDismiss, setToDismiss] = useState<ProspectSignal | null>(null);

  const signalsQuery = useQuery({
    queryKey: ['prospect-signals', selectedProductId, { minScore, painType, status, page }],
    queryFn: () =>
      prospectSignalsService.list({
        productId: selectedProductId!,
        minScore,
        painType: painType || undefined,
        status,
        page,
      }),
    enabled: !!selectedProductId,
  });

  // Mesmas queryKeys do PipelineBoard, então o cache é compartilhado em vez de
  // refetchado só para o modal.
  const stagesQuery = useQuery({
    queryKey: ['product', 'stages', selectedProductId],
    queryFn: () => productsService.getStages(selectedProductId!),
    enabled: !!selectedProductId && !!toPromote,
  });

  const usersQuery = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: () => usersService.list({ limit: 100 }),
    enabled: !!toPromote,
  });

  function invalidateSignals() {
    void queryClient.invalidateQueries({ queryKey: ['prospect-signals'] });
  }

  const promoteMutation = useMutation({
    mutationFn: (vars: { id: number; stageId: number; assignedUserId: number }) =>
      prospectSignalsService.promote(vars.id, {
        stageId: vars.stageId,
        assignedUserId: vars.assignedUserId,
      }),
    onSuccess: () => {
      toast.success('Sinal promovido a lead');
      setToPromote(null);
      invalidateSignals();
      // Sem isto o lead recém-criado não aparece no pipeline até um reload.
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: number) => prospectSignalsService.dismiss(id),
    onSuccess: () => {
      toast.success('Sinal dispensado');
      setToDismiss(null);
      invalidateSignals();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (!selectedProductId) {
    return (
      <EmptyState
        illustration={<Inbox className="w-10 h-10" />}
        title="Selecione um produto"
        body="Os sinais de prospecção são escopados por produto. Escolha um produto para ver a fila de triagem."
      />
    );
  }

  const signals = signalsQuery.data?.data ?? [];
  const meta = signalsQuery.data?.meta;
  const hasActiveFilter = minScore > 0 || painType !== '' || status !== 'pending';
  const busyId = promoteMutation.isPending
    ? promoteMutation.variables?.id
    : dismissMutation.isPending
      ? dismissMutation.variables
      : undefined;

  const stages: Array<{ id: number; name: string }> = Array.isArray(stagesQuery.data)
    ? stagesQuery.data
    : (stagesQuery.data?.data ?? []);
  // `usersService.list` devolve `{ users, total, page, ... }` — a chave é
  // `users`, não `data` como nos outros services paginados do CRM.
  const users: Array<{ id: number; name: string }> = usersQuery.data?.users ?? [];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl font-semibold text-ink-primary">Sinais de prospecção</h1>
        <p className="text-sm text-ink-secondary">
          Dor qualificada encontrada em comunidades públicas, classificada e pronta para triagem.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-4">
        {/* `Input` já renderiza o próprio <label> com htmlFor ligado ao input. */}
        <Input
          label="Score mínimo"
          type="number"
          min={0}
          max={100}
          className="w-24"
          value={String(minScore)}
          onChange={(e) => {
            setMinScore(Number(e.target.value || 0));
            setPage(1);
          }}
        />

        <div className="flex flex-col gap-1">
          <label htmlFor="filter-pain-type" className="text-xs font-medium text-ink-secondary">
            Tipo de dor
          </label>
          <select
            id="filter-pain-type"
            className={selectClass}
            value={painType}
            onChange={(e) => {
              setPainType(e.target.value as ProspectPainTypeValue | '');
              setPage(1);
            }}
          >
            <option value="">Todos</option>
            {PROSPECT_PAIN_TYPES.map((value) => (
              <option key={value} value={value}>
                {PROSPECT_PAIN_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="filter-status" className="text-xs font-medium text-ink-secondary">
            Status
          </label>
          <select
            id="filter-status"
            className={selectClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ProspectSignalStatus);
              setPage(1);
            }}
          >
            {PROSPECT_SIGNAL_STATUSES.map((value) => (
              <option key={value} value={value}>
                {PROSPECT_SIGNAL_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {signalsQuery.isPending && (
        <div className="flex flex-col gap-3">
          <Skeleton height={140} />
          <Skeleton height={140} />
          <Skeleton height={140} />
        </div>
      )}

      {signalsQuery.isError && (
        <EmptyState
          title="Não foi possível carregar os sinais"
          body={errorMessage(signalsQuery.error)}
          action={<Button onClick={() => void signalsQuery.refetch()}>Tentar novamente</Button>}
        />
      )}

      {signalsQuery.isSuccess && signals.length === 0 && (
        <EmptyState
          illustration={<Inbox className="w-10 h-10" />}
          title={
            hasActiveFilter
              ? `Nenhum sinal acima de ${minScore}`
              : 'Nenhuma rodada de prospecção gravou sinais ainda'
          }
          body={
            hasActiveFilter
              ? 'Baixe o score mínimo ou troque o status para ver mais.'
              : 'Quando uma rodada rodar e classificar dor qualificada, os sinais aparecem aqui.'
          }
          {...(hasActiveFilter
            ? {
                action: (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setMinScore(0);
                      setPainType('');
                      setStatus('pending');
                      setPage(1);
                    }}
                  >
                    Limpar filtros
                  </Button>
                ),
              }
            : {})}
        />
      )}

      {signals.length > 0 && (
        <div className="flex flex-col gap-3">
          {signals.map((s) => (
            <SignalCard
              key={s.id}
              signal={s}
              isBusy={busyId === s.id}
              onPromote={setToPromote}
              onDismiss={setToDismiss}
            />
          ))}
        </div>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <span className="text-sm text-ink-secondary">
            Página {meta.page} de {meta.totalPages} · {meta.total} sinais
          </span>
          <Button
            variant="secondary"
            disabled={page >= meta.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      )}

      <PromoteSignalModal
        signal={toPromote}
        stages={stages}
        users={users}
        isPending={promoteMutation.isPending}
        onClose={() => setToPromote(null)}
        onConfirm={({ stageId, assignedUserId }) => {
          if (toPromote) {
            promoteMutation.mutate({ id: toPromote.id, stageId, assignedUserId });
          }
        }}
      />

      {/*
        Dispensar não tem rota de undo no backend: o dismissedAt é gravado e
        pronto. A confirmação é a única proteção contra descarte acidental.
      */}
      {toDismiss && (
        <Modal
          open
          onClose={() => setToDismiss(null)}
          title={`Dispensar @${toDismiss.authorHandle}?`}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setToDismiss(null)}
                disabled={dismissMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                disabled={dismissMutation.isPending}
                onClick={() => dismissMutation.mutate(toDismiss.id)}
              >
                Confirmar dispensa
              </Button>
            </div>
          }
        >
          <p className="text-sm text-ink-secondary">
            O sinal sai da fila de triagem. <strong>Não há como reverter pela tela</strong> — ele só
            volta a aparecer no filtro de dispensados.
          </p>
        </Modal>
      )}
    </div>
  );
}
